import { api } from './api';

/**
 * 웹푸시 클라이언트 헬퍼. iOS는 홈 화면에 추가된 PWA(standalone)에서만 동작하고,
 * 권한 요청은 반드시 사용자 제스처(버튼 클릭) 안에서 호출해야 한다.
 */

export type PushState =
  | 'unsupported' // 브라우저가 푸시 미지원
  | 'needs-install' // iOS인데 홈 화면 미설치 → 설치 후 가능
  | 'default' // 아직 권한 요청 전(켤 수 있음)
  | 'denied' // 사용자가 차단(기기 설정에서만 해제)
  | 'subscribed'; // 켜짐

export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

/** 홈 화면에 추가된 PWA(standalone)로 실행 중인지. */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export async function getPushState(): Promise<PushState> {
  // iOS는 설치(standalone) 전엔 PushManager가 없으므로 먼저 안내.
  if (isIos() && !isStandalone()) return 'needs-install';
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission === 'default') return 'default';
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return sub ? 'subscribed' : 'default';
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/** 권한 요청 → 구독 → 서버 등록. 반드시 클릭 핸들러 안에서 호출. */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) throw new Error('이 기기는 알림을 지원하지 않아요.');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'default';

  const { publicKey } = await api.get<{ publicKey: string }>('/api/push/vapid');
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });
  }
  const json = sub.toJSON();
  await api.post('/api/push/subscribe', {
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys!.p256dh, auth: json.keys!.auth },
  });
  return 'subscribed';
}

/** 구독 해제 + 서버에서 제거. */
export async function disablePush(): Promise<PushState> {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api.post('/api/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
  return 'default';
}

export async function sendTestPush(): Promise<void> {
  await api.post('/api/push/test');
}
